import React, { useRef, useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FocalPoint, Tooltip } from '@yola/ws-ui';
import { utils, view, assets } from '@yola/ws-sdk';
import throttle from 'lodash.throttle';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import getBreakpointTypeTraitValue from 'src/js/modules/extensions/common/helpers/get-breakpoint-type-trait-value';
import ControlPane from 'src/js/modules/control-pane/components/control-pane';
import TriggerEventTracker from 'src/js/modules/utils/trigger-event-tracker';
import segment from 'src/js/modules/analytics/segment';
import customUI from 'src/js/modules/custom-ui';
import topbar from 'src/js/modules/topbar';
import getDefaultTraits from 'src/js/modules/analytics/segment/helpers/get-default-traits';
import toPositionPercents from 'src/js/modules/extensions/registry/ws-composite-group/helpers/to-position-percents.js';
import clearRepositionedImageInlineStyles from 'src/js/modules/extensions/registry/ws-image/helpers/clear-repositioned-image-inline-styles.js';
import shouldKeepInlineStylesUntilViewReload from 'src/js/modules/extensions/registry/ws-image/helpers/should-keep-inline-styles-until-view-reload';
import designAutomation from 'src/js/modules/design-automation';
import getBreakpointForCurrentDevice from 'src/js/modules/breakpoint-editing/helpers/get-breakpoint-for-current-device';
import { DESKTOP_BREAKPOINT } from 'src/js/modules/breakpoint-editing/constants/breakpoints';
import withFocalPointContext from '../hoc/with-focal-point-context';
import constants from '../constants/common';
import operationTypes from '../constants/operation-types';
import helpers from '../helpers';
import verifiers from '../verifiers';
import ids from '../constants/triggers-ids';
import MediaBlockComponent from '../components/media-block-component';
import useFocalPoint from '../hooks/use-focal-point';
import { getSettings } from '../settings';
import FocalPointImageResizer from '../components/focal-point-image-resizer';
import getImageTypeTrait from '../helpers/get-image-type-trait';

const {
  track,
  constants: { events },
} = segment;

const { useWaitForAutomation } = designAutomation.hooks;
const { ZOOM_STEP, FOCAL_POINT_AREA_TRIGGER, SUBMIT_TRIGGER } = constants;
const { addBaseHref } = assets.helpers;

/* eslint no-shadow: "off" */
/* eslint no-param-reassign: "off" */
/* eslint no-console: "off" */

function InlineImageContainer({
  elementId,
  onOverlayClick = utils.noop,
  onSubmit = utils.noop,
  onDrag: onDragProp = utils.noop,
  onDragStart: onDragStartProp = utils.noop,
  onDragEnd: onDragEndProp = utils.noop,
  onScale = utils.noop,
  width: viewportWidth = null,
  height: viewportHeight = null,
  orientation = null,
  onCancel = utils.noop,
  shouldApplyChangesOnSubmit = true,
  preserveSharedData = false,
}) {
  const waitForAutomationThen = useWaitForAutomation();
  const scaleBeforeChange = useRef(null);
  const previousImagePosition = useRef({ x: 0, y: 0 });
  const settings = useMemo(getSettings, []);
  const topbarHeight = useSelector(topbar.selectors.getHeight);

  const [featureFlags] = useFeatureFlags(['breakpoint_editing']);
  const { breakpoint_editing: isBreakpointEditingEnabled } = featureFlags;
  const activeBreakpoint = isBreakpointEditingEnabled
    ? getBreakpointForCurrentDevice()
    : DESKTOP_BREAKPOINT;
  const breakpointType = isBreakpointEditingEnabled
    ? getBreakpointTypeTraitValue(activeBreakpoint)
    : undefined;

  const [imageOffset, setImageOffset] = useState({ x: 50, y: 50 });

  // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  const onPositionChange = useCallback(
    throttle(({ x, y }) => {
      setImageOffset({ x, y });
    }, 200),
    []
  );

  const onInitialImageLoad = useCallback(({ imageNode, initialData, imageData, width, height }) => {
    const isGalleryItem = helpers.isGalleryItem(imageNode);
    const initialOriginalGalleryItemSize = helpers.getOriginImageSize(imageNode);
    const { height: initialContainerHeight } = helpers.getContainerBounds(imageNode);

    const imageWidth = isGalleryItem ? initialOriginalGalleryItemSize.width : width;
    const imageHeight = isGalleryItem ? initialOriginalGalleryItemSize.height : height;

    initialData.current.width = imageWidth;
    initialData.current.height = imageHeight;
    initialData.current.containerHeight = initialContainerHeight;

    imageData.current.width = imageWidth;
    imageData.current.height = imageHeight;

    track(events.FOCAL_POINT_SETTINGS_DISPLAYED, {
      ...getDefaultTraits(elementId),
      imageType: getImageTypeTrait(elementId),
      ...(breakpointType && { breakpointType }),
    });
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const onFitImageSize = useCallback(
    ({ oldZoom, newZoom }) => {
      track(events.FOCAL_POINT_FIT_IN_BUTTON_CLICKED, {
        ...getDefaultTraits(elementId),
        oldZoom,
        newZoom,
        imageType: getImageTypeTrait(elementId),
        ...(breakpointType && { breakpointType }),
      });
    },
    [elementId]
  );

  const onDragStart = useCallback(
    (...args) => {
      const [, , { position }] = args;
      previousImagePosition.current = {
        x: position.x,
        y: position.y,
      };
      onDragStartProp(...args);
    },
    [onDragStartProp]
  );

  const onDrag = useCallback(
    (...args) => {
      const [offsetInPercents] = args;

      onDragProp(...args);
      onPositionChange({
        x: offsetInPercents.xInPercents,
        y: offsetInPercents.yInPercents,
      });
    },
    [onDragProp, onPositionChange]
  );

  const onDragEnd = useCallback(
    (...args) => {
      onDragEndProp(...args);
      const [{ x, y }] = args;
      const { x: prevX, y: prevY } = previousImagePosition.current;
      if (prevX !== x || prevY !== y) {
        track(events.FOCAL_POINT_IMAGE_POSITION_ADJUSTED, {
          ...getDefaultTraits(elementId),
          imageType: getImageTypeTrait(elementId),
          ...(breakpointType && { breakpointType }),
        });
      }
      previousImagePosition.current = { x, y };
    },
    [elementId, onDragEndProp]
  );

  const {
    position,
    scale,
    zoom,
    imageBounds,
    initialData,
    imageData,
    controlsRef,
    isLoadingImage,
    imageNode,
    imageComputedStyles,
    handleImageDrag,
    handleImageDragStart,
    handleImageDragEnd,
    handleScale,
    handleFitImageSize,
    setImageBounds,
    currentSource,
  } = useFocalPoint({
    elementId,
    onInitialImageLoad,
    onFitImageSize,
    viewportWidth,
    viewportHeight,
    orientation,
    onDrag,
    onDragStart,
    onDragEnd,
    onScale,
    preserveSharedData,
    getInitialScrollPosition: (isHigherThanViewport) => ({
      sideToScroll: isHigherThanViewport ? 'bottom' : 'center',
      offset: isHigherThanViewport ? topbarHeight * 2 : 0,
    }),
  });

  const isGalleryItem = useMemo(() => helpers.isGalleryItem(imageNode), [imageNode]);

  const handleCancel = useCallback(() => {
    if (isLoadingImage.current) return;
    const {
      scale: initialScale,
      position: initialPosition,
      source: initialSource,
      width: initialImageWidth,
      height: initialImageHeight,
      ratio: initialRatio,
    } = initialData.current;

    helpers.setRatio(imageNode, initialRatio);
    helpers.setScale(imageNode, initialScale);
    helpers.setPosition(imageNode, toPositionPercents(initialPosition));
    clearRepositionedImageInlineStyles(imageNode);
    helpers.setCurrentImageUrl(imageNode, initialSource);

    if (isGalleryItem) {
      helpers.setOriginImageSize(imageNode, {
        width: initialImageWidth,
        height: initialImageHeight,
      });
    }

    track(events.FOCAL_POINT_SETTINGS_CANCELLED, {
      ...getDefaultTraits(elementId),
      imageType: getImageTypeTrait(elementId),
      ...(breakpointType && { breakpointType }),
    });

    customUI.operations.hide();
    onOverlayClick();
    onCancel();
  }, [elementId, isLoadingImage, onCancel, isGalleryItem, imageNode, initialData, onOverlayClick]);

  const handleSubmit = useCallback(() => {
    const { ratio: initialRatio } = initialData.current;

    const setSrcOperation = helpers.getViewOperation(operationTypes.SRC, elementId, currentSource);
    const position = helpers.getPosition(imageNode);
    const setPositionOperation = helpers.getViewOperation(
      operationTypes.POSITION,
      elementId,
      toPositionPercents(position),
      activeBreakpoint
    );
    const setScaleOperation = helpers.getViewOperation(
      operationTypes.SCALE,
      elementId,
      scale,
      activeBreakpoint
    );

    let operations = [setSrcOperation, setPositionOperation, setScaleOperation];

    if (initialRatio) {
      const currentRatio = imageNode.getAttribute('aspect-ratio');
      if (initialRatio !== currentRatio) {
        operations.push(
          helpers.getViewOperation(operationTypes.ASPECT_RATIO, elementId, currentRatio)
        );
      }
    }

    if (helpers.isGalleryItem(imageNode)) {
      const { width, height } = imageData.current;
      const setOriginSrcOperation = helpers.getViewOperation(
        operationTypes.ORIGIN_SRC,
        elementId,
        currentSource
      );
      const setOriginWidthOperation = helpers.getViewOperation(
        operationTypes.ORIGIN_WIDTH,
        elementId,
        width
      );
      const setOriginHeightOperation = helpers.getViewOperation(
        operationTypes.ORIGIN_HEIGHT,
        elementId,
        height
      );

      operations = [
        ...operations,
        setOriginSrcOperation,
        setOriginWidthOperation,
        setOriginHeightOperation,
      ];
    }

    if (shouldApplyChangesOnSubmit) {
      view.operations.bulkViewOperations(operations);
      if (shouldKeepInlineStylesUntilViewReload(imageNode)) {
        // Keep the live preview until reload. Drop it from the static copy so the
        // reloaded node, and a later image replace, do not inherit the old scale.
        const staticElement = view.accessors.getStaticElement(elementId);
        clearRepositionedImageInlineStyles(staticElement, { immediate: true });
      } else {
        clearRepositionedImageInlineStyles(imageNode);
      }
    }
    onSubmit({ operations });

    customUI.operations.hide();
    Tooltip.hide();
  }, [
    shouldApplyChangesOnSubmit,
    waitForAutomationThen,
    initialData,
    imageData,
    onSubmit,
    elementId,
    imageNode,
    scale,
    currentSource,
    activeBreakpoint,
  ]);

  const withSubmitAnalyticsEvent = useCallback(
    (cb, triggerId) => () => {
      const { scale: initialScale, position: initialPosition } = initialData.current;
      const { height: containerHeight } = helpers.getContainerBounds(imageNode);
      cb();

      track(events.FOCAL_POINT_SETTINGS_SUBMITTED, {
        ...getDefaultTraits(elementId),
        oldZoom: initialScale,
        newZoom: scale,
        zoomAdjusted: initialScale !== scale,
        positionAdjusted: initialPosition.x !== position.x || initialPosition.y !== position.y,
        imageType: getImageTypeTrait(elementId),
        triggerId,
        oldHeight: initialData.current.containerHeight,
        newHeight: containerHeight,
        containerResized: initialData.current.containerHeight !== containerHeight,
        ...(breakpointType && { breakpointType }),
      });
    },
    [elementId, imageNode, position, initialData, scale]
  );

  const getMediaSource = useCallback(() => addBaseHref(currentSource), [currentSource]);

  const saveInitialSliderValueBeforeChange = useCallback(() => {
    // RCSlider component do not guarantee that `onBeforeChange` callback would run once
    // before `onAfterChange`. We've faced a use case when it's called once again between
    // `onChange`  and `onAfterChange` so we get wrong value. That's why we set initial
    // value only if it's empty, and empty it on purpose inside `onAfterChange` callback handler.
    if (scaleBeforeChange.current === null) {
      scaleBeforeChange.current = helpers.getScale(imageNode);
    }
  }, [imageNode]);

  const trackSliderChanges = useCallback(() => {
    if (scaleBeforeChange.current === null) return;

    const scale = helpers.getScale(imageNode);
    if (scale !== scaleBeforeChange.current) {
      track(events.FOCAL_POINT_ZOOM_SLIDER_ADJUSTED, {
        ...getDefaultTraits(elementId),
        oldZoom: scaleBeforeChange.current,
        newZoom: scale,
        imageType: getImageTypeTrait(elementId),
        ...(breakpointType && { breakpointType }),
      });
    }

    scaleBeforeChange.current = null;
  }, [imageNode, elementId]);

  const imageZoomSliderControl = helpers.getZoomSliderControl({
    id: ids.REGULAR_IMAGE_ZOOM_SLIDER,
    scale,
    zoom,
    onBeforeChange: saveInitialSliderValueBeforeChange,
    onAfterChange: trackSliderChanges,
    onChange: (...args) => {
      saveInitialSliderValueBeforeChange();
      handleScale(...args);
    },
  });

  const imageSubmitControl = helpers.getSubmitControl({
    id: ids.REGULAR_IMAGE_SUBMIT,
    onClick: withSubmitAnalyticsEvent(handleSubmit, SUBMIT_TRIGGER),
  });

  const fitControl = helpers.getFitControl({
    id: ids.REGULAR_IMAGE_FIT_TRIGGER,
    onClick: handleFitImageSize,
  });

  const imageContainerControls = [
    helpers.getZoomOutControl({
      id: ids.REGULAR_IMAGE_ZOOM_OUT_TRIGGER,
      onClick: () => {
        Tooltip.hide();
        const oldZoom = helpers.getScale(imageNode);
        handleScale(zoom - ZOOM_STEP);
        track(events.FOCAL_POINT_ZOOM_OUT_BUTTON_CLICKED, {
          ...getDefaultTraits(elementId),
          oldZoom,
          newZoom: helpers.getScale(imageNode),
          imageType: getImageTypeTrait(elementId),
          ...(breakpointType && { breakpointType }),
        });
      },
    }),
    imageZoomSliderControl,
    helpers.getZoomInControl({
      id: ids.REGULAR_IMAGE_ZOOM_IN_TRIGGER,
      onClick: () => {
        const oldZoom = helpers.getScale(imageNode);
        Tooltip.hide();
        handleScale(zoom + ZOOM_STEP);
        track(events.FOCAL_POINT_ZOOM_IN_BUTTON_CLICKED, {
          ...getDefaultTraits(elementId),
          oldZoom,
          newZoom: helpers.getScale(imageNode),
          imageType: getImageTypeTrait(elementId),
          ...(breakpointType && { breakpointType }),
        });
      },
    }),
    fitControl,
    imageSubmitControl,
  ];

  const controlsProps = useMemo(() => {
    if (!imageBounds) return null;
    const hasNoSpaceForControl = imageBounds && imageBounds.top < topbarHeight * 2;
    const direction = !hasNoSpaceForControl ? 'up' : 'none';
    return {
      position: helpers.getControlsPosition(imageBounds, true, imageContainerControls),
      direction,
    };
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [imageBounds, imageContainerControls]);

  useEffect(() => {
    const [x, y] = helpers.getPosition(imageNode);
    setImageOffset({ x, y });
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  if (imageNode && imageNode.nodeName.toLowerCase() !== settings.imageContainer) {
    console.warn(
      `You can't use focal point with any other node type except ${settings.imageContainer}`
    );
    return null;
  }

  if (!imageBounds) return null;

  const {
    position: { offsetX, offsetY },
  } = controlsProps;

  const controls = (
    <TriggerEventTracker>
      <ControlPane
        controlPaneRef={controlsRef}
        offsetX={offsetX}
        offsetY={offsetY}
        direction={controlsProps.direction}
        content={imageContainerControls}
        expand="down"
        className="ws-focal-point-controls"
      />
    </TriggerEventTracker>
  );

  return (
    <FocalPoint
      controls={controls}
      onOverlayClick={handleCancel}
      onOverlayDoubleClick={withSubmitAnalyticsEvent(handleSubmit, FOCAL_POINT_AREA_TRIGGER)}
      onDrag={handleImageDrag}
      onDragStart={handleImageDragStart}
      onDragEnd={handleImageDragEnd}
      targetElementBounds={imageBounds}
      elementComputedStyles={imageComputedStyles}
      tooltipText={`X:${imageOffset.x}% Y:${imageOffset.y}%`}
      topbarHeight={topbarHeight}
    >
      <MediaBlockComponent
        mediaContainerRef={imageNode}
        targetElementBounds={imageBounds}
        mediaSource={getMediaSource()}
      />
      {verifiers.isResizable(imageNode) && (
        <FocalPointImageResizer
          elementId={elementId}
          onImageResize={setImageBounds}
          targetElementBounds={imageBounds}
        />
      )}
    </FocalPoint>
  );
}

InlineImageContainer.propTypes = {
  elementId: PropTypes.string.isRequired,
  onOverlayClick: PropTypes.func,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onDrag: PropTypes.func,
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func,
  onScale: PropTypes.func,
  width: PropTypes.number,
  height: PropTypes.number,
  orientation: PropTypes.string,
  preserveSharedData: PropTypes.bool,
  shouldApplyChangesOnSubmit: PropTypes.bool,
};

export default withFocalPointContext(InlineImageContainer);
